import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell, Line } from 'recharts';
import { BriefcaseIcon, SlidersHorizontal } from 'lucide-react';
import { formatCurrencyAmount, formatNumberValue, getMonthNameFromDate } from '../../../../../utils/miscFunctions';
import SelectDropdown from '../../../../../components/form-control/SelectDropdown';
import DatePickerInput from '../../../../../components/form-control/DatePickerInput';
import { getBrokerDashboardInsurerDistribution } from '../../../../../api/requests';
import { useQuery } from '@tanstack/react-query';
import useUserAccount from '../../../../../hooks/userAccountHook';
import { getSelectedCountry } from '../../../../../states/slices/resourceSlice';
import { useSelector } from 'react-redux';
import DashboardDatePickers, { SelectedDateRange } from '../../../../../components/dashboard/date-range-selector/datePicker';
import moment from 'moment';
import Button from '../../../../../components/form-control/Button';

interface InsuranceCompanyData {
  insurers: string[];
  policyVolumes: number[];
  premiumAmounts: number[];
  commissionAmounts: number[];
}


type MetricType = 'policies' | 'premium' | 'commission';

const InsuranceCompanyDistribution = ({ companyId: companyIdProp }: { companyId?: number } = {}) => {
  const [metricType, setMetricType] = useState<MetricType>('policies')
  const { user } = useUserAccount();
  const selectedCountry = useSelector(getSelectedCountry);
  const countryId = selectedCountry.id || 1;
  const currency = countryId === 1 ? 'GHS' : 'NGN';
  const [selectedYear, setSelectedYear] = useState<Date | null>(new Date(moment().year(), 0, 1));
  const [selectedYearMonth, setSelectedYearMonth] = useState<Date | null>(null);
  const getInsurerDistributionData = async () => {
    const year = moment(selectedYear).format('YYYY');
    const month = selectedYearMonth ? getMonthNameFromDate(selectedYearMonth) : undefined;
    // When embedded in an admin "team detail" view a specific companyId is passed
    // in; fall back to the logged-in user's own company for the normal broker
    // dashboard. Without this the chart always showed the admin's parent company.
    const companyId = companyIdProp ?? user?.company?.id

    try {
      if (companyId) {
        const res: any = await getBrokerDashboardInsurerDistribution(Number(companyId), month, Number(year));
       
        if (res) {
          const data = res?.data;
          return {
            insurers: data?.insurers || [],
            policyVolumes: data?.policyVolumes || [],
            premiumAmounts: data?.premiumAmounts || [],
            commissionAmounts: data?.commissionAmounts || []
          }
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  const getMetricLabel = () => {
    switch (metricType) {
      case 'policies': return 'Policies Count';
      case 'premium': return `Premium Amount (${currency})`;
      case 'commission': return `Commission Earned (${currency})`;
    }
  };

  const { data, isLoading: isLoadingInsurerDistribution } = useQuery({
    queryKey: ['getInsurerDistributionData', selectedYear, selectedYearMonth, companyIdProp],
    queryFn: getInsurerDistributionData,
    // Refetch on mount so the dashboard reflects edits without a full reload,
    // overriding the app-wide 5-min staleTime (src/lib/queryClient.ts).
    refetchOnMount: 'always'
  })

  // Define colors for the chart
  const colors = [
    '#c7b8ea', // indigo-100
    '#34d399', // emerald-100
    '#feb546', // amber-100
    '#f87171', // red-100
    '#c084fc', // violet-100
    '#f9a8d4', // pink-100
  ];

  // Transform data for the chart
  const chartData = data?.insurers?.map((insurer: string, index: number) => ({
    name: insurer,
    policies: data?.policyVolumes?.[index] || 0,
    premium: data?.premiumAmounts?.[index] || 0,
    commission: data?.commissionAmounts?.[index] || 0,
    color: colors[index % colors.length],
  })) || [];

const heightValue = (num: number): string => {
  if (num < 5) return 'h-72';
  if (num < 10) return 'h-[350px]';
  if (num < 15) return 'h-[450px]';
  if (num < 20) return 'h-[580px]';
  if (num < 25) return 'h-[650px]';
  return 'h-[700px]'; // fallback for 25 or more
};

  // Sort data based on selected metric
  const sortedData = [...chartData].sort((a, b) => (b[metricType] || 0) - (a[metricType] || 0));

  // Calculate totals for summary stats
  const totalPolicies = data?.policyVolumes?.reduce((sum: number, val: number) => sum + (val || 0), 0) || 0;
  const totalPremium = data?.premiumAmounts?.reduce((sum: number, val: number) => sum + (val || 0), 0) || 0;
  const totalCommission = data?.commissionAmounts?.reduce((sum: number, val: number) => sum + (val || 0), 0) || 0;

  // Calculate percentages for top company
  const topCompany = sortedData[0];
  const topCompanyPercentage = topCompany && totalPolicies > 0
    ? ((topCompany[metricType] / (metricType === 'policies' ? totalPolicies :
      metricType === 'premium' ? totalPremium : totalCommission)) * 100).toFixed(1)
    : '0';


  // Loading state
  if (isLoadingInsurerDistribution) {
    return (
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 h-full flex flex-col">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Insurance Company Distribution</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">Loading data for {String(selectedYear)}</p>
          </div>
          <div className="w-40 h-9 bg-gray-100 dark:bg-gray-800 rounded-md animate-pulse"></div>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center bg-gray-50 dark:bg-gray-800 rounded-lg">
          <div className="space-y-2 text-center">
            <div className="w-12 h-12 mx-auto rounded-full bg-gray-100 dark:bg-gray-800 animate-pulse"></div>
            <p className="text-gray-500 dark:text-gray-400">Loading insurance data...</p>
          </div>
        </div>
      </div>
    );
  }

  // Empty state
  if (!data || !data.insurers || data.insurers.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border dark:bg-transparent border-gray-200 dark:border-gray-700 p-6 h-full flex flex-col">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Insurance Company Distribution</h3>
          </div>
          <div className="flex gap-2 items-center justify-between py-2">
                   <DatePickerInput
              id="month"
              selected={selectedYearMonth}
              onChange={(date: Date | null) => date && setSelectedYearMonth(date)}
              showMonthYearPicker
              dateFormat="MMM yyyy"
              placeholder="Select month"
              maxDate={new Date()}
              classes="w-full"
            />
            <DatePickerInput
              id="month"
              selected={selectedYear}
              onChange={(date: Date | null) => {
                if (date) {
                  setSelectedYear(date);
                }
              }}
              maxDate={new Date()}
              classes="max-w-[150px] !py-[6px]"
              placeholder="Select year"
              showYearOnly
            />
            <Button
                label="Reset"
                onClick={() => {
                    setSelectedYear(new Date(moment().year(), 0, 1));
                    setSelectedYearMonth(null);
                }}
                bgColor="primary"
                className="w-full"
            />

           
          </div>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center bg-gray-50 dark:bg-transparent rounded-lg p-6 text-center">
          <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-3">
            <BriefcaseIcon className="w-8 h-8 text-gray-400 dark:text-gray-400" />
          </div>
          <h4 className="font-medium text-gray-900 dark:text-white mb-1">No insurance data</h4>
          <p className="text-sm text-gray-500 dark:text-gray-400 max-w-md">
            There are no insurance company records available for {moment(selectedYear).format('YYYY')}.
            Try selecting a different month or check back later for updates.
          </p>
        </div>
      </div>
    );
  }
const CustomLegend = (value:any) => (
  <div className='flex items-center justify-center w-full'>
    <span className='w-[12px] h-[12px] bg-black dark:bg-white mr-2' />
    <span className='text-black dark:text-white' >
      {value}
    </span>
  </div>
);
  return (
    <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border h-full border-gray-200 dark:border-gray-700 p-6">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Insurance Company Distribution</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">Policy distribution across insurance providers</p>
        </div>

        <div className="flex items-center space-x-4">
          <SelectDropdown
            list={[
              { value: 'policies', name: 'Policies' },
              { value: 'premium', name: 'Premium' },
              { value: 'commission', name: 'Commission' },
            ]}
            listSelectedValue={metricType}
            onChange={(e: string) => setMetricType(e as MetricType)}
            wrapperClasses='w-36'
          />
            <div className="flex gap-2 items-center justify-between py-2">
              <DatePickerInput
                id="month"
                selected={selectedYearMonth}
                onChange={(date: Date | null) => date && setSelectedYearMonth(date)}
                showMonthYearPicker
                dateFormat="MMM yyyy"
                placeholder="Select month"
                maxDate={new Date()}
                classes="w-full"
              />
              <DatePickerInput
                id="month"
                selected={selectedYear}
                onChange={(date: Date | null) => {
                  if (date) {
                    setSelectedYear(date);
                  }
                }}
                maxDate={new Date()}
                classes="max-w-[150px] !py-[6px]"
                placeholder="Select year"
                showYearOnly
              />
              <Button
                label="Reset"
                onClick={() => {
                    setSelectedYear(new Date(moment().year(), 0, 1));
                    setSelectedYearMonth(null);
                }}
                bgColor="primary"
                className="w-fit"
              />
          </div>

         
        </div>
      </div>

      <div className={`${heightValue(sortedData?.length)} overflow-y-auto`}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={sortedData}
            layout="vertical"
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
            <XAxis
              type="number"
              tickFormatter={(value: number) => metricType === 'policies' ? `${value}` : formatCurrencyAmount(value,undefined,false)}
              tick={{ fontSize: 12 }}
             
            />
            <YAxis
              type="category"
              dataKey="name"
              tick={{ fontSize: 12 }}
              width={100}
              interval={0}
            />
            <Tooltip
              formatter={((value: number) => metricType === 'policies' ? `${value}` : formatCurrencyAmount(value,undefined,false))}
              labelStyle={{ fontWeight: 'bold' }}
            />
            <Legend content={CustomLegend(getMetricLabel())}/>
            <Bar
              dataKey={metricType}
              name={getMetricLabel()}
              radius={[0, 4, 4, 0]}
              barSize={22}
            >
              {sortedData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="mt-6 grid grid-cols-3 gap-4">
        <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600 dark:text-gray-400">Policies</div>
            <BriefcaseIcon size={16} className="text-blue-600" />
          </div>
          <p className="text-lg font-bold text-gray-900 dark:text-white mt-1">
            {formatNumberValue(totalPolicies)}
          </p>
        </div>
        <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600 dark:text-gray-400">Premium {`(${currency})`}</div>
            <BriefcaseIcon size={16} className="text-green-600" />
          </div>
          <p className="text-lg font-bold text-gray-900 dark:text-white mt-1">
            { totalPremium ? formatCurrencyAmount(totalPremium,undefined,false) : '-'}
          </p>
        </div>
        <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600 dark:text-gray-400">Commission {`(${currency})`}</div>
            <BriefcaseIcon size={16} className="text-yellow-600" />
          </div>
          <p className="text-lg font-bold text-gray-900 dark:text-white mt-1">
            {formatCurrencyAmount(totalCommission,undefined, false)}
          </p>
        </div>
      </div>

      {topCompany && (
        <div className="mt-4 flex items-center justify-between px-2">
          <div>
            <span className="text-sm font-medium text-blue-700 dark:text-blue-400">Top provider:</span>
            <span className="ml-2 font-medium dark:text-white">{topCompany.name}</span>
            <span className="ml-1 text-sm text-gray-500 dark:text-white">({topCompanyPercentage}% of all {metricType}) </span>
          </div>
          {/* <div className="flex items-center bg-blue-50 px-3 py-1 rounded-full">
            <span className="text-sm font-medium text-blue-700">{topCompanyPercentage}% of {metricType}</span>
          </div> */}
        </div>
      )}
    </div>
  );
};

export default InsuranceCompanyDistribution;
